import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { money, daysAgo } from "@/lib/format";
import { toast } from "sonner";
import { Star, Wand2 } from "lucide-react";

const STATUSES = ["all", "intake", "cleaning", "photography", "assessment", "pricing", "ready_to_list", "live", "reserved", "sold", "returned", "archived", "written_off"];
const POSITIONS = ["front", "three_quarter_left", "left_side", "folded", "extra"];
const MAX_PHOTOS = 5;
const INSPECTION_FIELDS = [
  ["authenticity_checked", "Authenticity Checked"],
  ["condition_graded", "Condition Graded"],
  ["ultrasonically_cleaned", "Ultrasonically Cleaned"],
  ["hinges_checked", "Hinges Checked"],
  ["alignment_checked", "Frame Alignment Checked"],
  ["individually_serviced", "Individually Serviced"],
  ["photographed", "Photographed"],
  ["ready_to_wear", "Ready To Wear"],
];
const AUTH_METHODS = [
  "Lookers Authentication Check",
  "Third-Party Authentication",
  "Documentation Verified",
  "Multiple Methods",
  "Pending",
  "Not Applicable",
];

function genId() {
  return (window.crypto?.randomUUID && window.crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const inputCls = "border border-zinc-200 rounded-md px-3 py-2 text-sm focus:border-teal-600 outline-none";

export default function AdminInventory() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState(params.get("status") || "all");
  const [search, setSearch] = useState(params.get("search") || "");
  const [result, setResult] = useState({ items: [], total: 0 });
  const [showCreate, setShowCreate] = useState(false);
  const [brands, setBrands] = useState([]);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const editFileRef = useRef(null);

  const load = () => api.get("/admin/inventory", { params: { status: status === "all" ? undefined : status, search: search || undefined, limit: 100 } }).then((res) => setResult(res.data));

  useEffect(() => { load(); }, [status, search]);
  useEffect(() => { api.get("/admin/brands").then((res) => setBrands(res.data)); }, []);

  const emptyForm = () => ({
    brand_slug: brands[0]?.slug || "", model: "", model_number: "", colour: "", frame_colour: "",
    frame_material: "Acetate", gender: "Unisex", style: "Classic", shape: "Round", size: "Medium",
    lens_width: "", bridge_width: "", temple_length: "", overall_frame_width: "",
    selling_price: "", cost_price: "", original_rrp: "", condition_score: 8, status: "live", location: "",
  });

  const openCreate = () => {
    setForm(emptyForm());
    setShowCreate(true);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/inventory", {
        ...form,
        selling_price: Number(form.selling_price),
        cost_price: Number(form.cost_price || 0),
        original_rrp: form.original_rrp ? Number(form.original_rrp) : null,
        condition_score: Number(form.condition_score),
        lens_width: form.lens_width ? Number(form.lens_width) : null,
        bridge_width: form.bridge_width ? Number(form.bridge_width) : null,
        temple_length: form.temple_length ? Number(form.temple_length) : null,
        overall_frame_width: form.overall_frame_width ? Number(form.overall_frame_width) : null,
      });
      toast.success("Product created");
      setShowCreate(false);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const duplicate = async (id) => {
    await api.post(`/admin/inventory/${id}/duplicate`);
    toast.success("Duplicated");
    load();
  };

  const quickStatus = async (id, newStatus) => {
    await api.put(`/admin/inventory/${id}`, { status: newStatus });
    load();
  };

  const toggleEditorsPick = async (id, current) => {
    await api.put(`/admin/inventory/${id}`, { is_editors_pick: !current });
    load();
  };

  const openEdit = async (id) => {
    const { data } = await api.get(`/admin/inventory/${id}`);
    setEditForm(data);
    setEditingId(id);
  };

  const uploadEditImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const current = editForm.images || [];
    if (current.length + files.length > MAX_PHOTOS) {
      toast.error(`Maximum ${MAX_PHOTOS} photos per product`);
      return;
    }
    setUploadingImg(true);
    try {
      const newImages = [...current];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newImages.push({ id: genId(), url: `${process.env.REACT_APP_BACKEND_URL}${data.url}`, position: POSITIONS[newImages.length] || "extra" });
      }
      setEditForm({ ...editForm, images: newImages });
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setUploadingImg(false);
      if (editFileRef.current) editFileRef.current.value = "";
    }
  };

  const removeEditImage = (imgId) => setEditForm({ ...editForm, images: editForm.images.filter((im) => im.id !== imgId) });

  const moveEditImage = (index, dir) => {
    const imgs = [...editForm.images];
    const newIndex = index + dir;
    if (newIndex < 0 || newIndex >= imgs.length) return;
    [imgs[index], imgs[newIndex]] = [imgs[newIndex], imgs[index]];
    setEditForm({ ...editForm, images: imgs });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/inventory/${editingId}`, {
        images: editForm.images,
        selling_price: Number(editForm.selling_price),
        cost_price: Number(editForm.cost_price),
        condition_score: Number(editForm.condition_score),
        status: editForm.status,
        lens_width: editForm.lens_width ? Number(editForm.lens_width) : null,
        bridge_width: editForm.bridge_width ? Number(editForm.bridge_width) : null,
        temple_length: editForm.temple_length ? Number(editForm.temple_length) : null,
        overall_frame_width: editForm.overall_frame_width ? Number(editForm.overall_frame_width) : null,
        rrp_source: editForm.rrp_source || "",
        rrp_source_url: editForm.rrp_source_url || "",
        rrp_verified: !!editForm.rrp_verified,
        inspection: editForm.inspection || {},
        authentication_method: editForm.authentication_method || "Lookers Authentication Check",
        authentication_provider: editForm.authentication_provider || null,
        authentication_date: editForm.authentication_date || null,
        authentication_reference: editForm.authentication_reference || null,
        authentication_verification_url: editForm.authentication_verification_url || null,
        original_case_included: !!editForm.original_case_included,
        whats_included: editForm.whats_included || [],
        condition_notes: editForm.condition_notes || "",
      });
      toast.success("Product updated");
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  return (
    <div className="p-8" data-testid="admin-inventory-page">
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-serif text-3xl text-zinc-900">Inventory</h1>
        <div className="flex gap-3">
          <Link to="/admin/inventory/new" className="flex items-center gap-1.5 bg-charcoal hover:bg-zinc-800 text-white px-5 py-2.5 text-xs font-heading font-semibold uppercase rounded-full transition-colors" data-testid="admin-wizard-button">
            <Wand2 size={14} /> Fast Listing Wizard
          </Link>
          <button onClick={openCreate} className="bg-teal-600 hover:bg-teal-700 text-ink px-5 py-2.5 text-xs font-heading font-semibold uppercase rounded-full transition-colors" data-testid="admin-new-product-button">+ New Product</button>
        </div>
      </div>

      <div className="flex gap-3 mb-6">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls} data-testid="admin-inventory-status-filter">
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <input placeholder="Search SKU, model, brand…" value={search} onChange={(e) => setSearch(e.target.value)} className={`${inputCls} flex-1`} data-testid="admin-inventory-search" />
      </div>

      <div className="text-sm text-zinc-400 mb-3">{result.total} items</div>
      <div className="bg-white border border-zinc-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-inventory-table">
          <thead className="bg-zinc-50 text-xs font-heading uppercase text-zinc-500 text-left">
            <tr>
              <th className="p-3">Inventory ID</th><th className="p-3">Brand / Model</th><th className="p-3">Photos</th><th className="p-3">Status</th>
              <th className="p-3">Cost</th><th className="p-3">Price</th><th className="p-3">Margin</th><th className="p-3">Days</th><th className="p-3">Pick</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {result.items.map((p) => (
              <tr key={p.id} className="border-t border-zinc-100 text-zinc-800" data-testid={`inventory-row-${p.inventory_id}`}>
                <td className="p-3 font-mono text-xs text-zinc-500">{p.inventory_id}</td>
                <td className="p-3">{p.brand_name} {p.model}</td>
                <td className="p-3">{(p.images || []).length}/{MAX_PHOTOS}</td>
                <td className="p-3">
                  <select value={p.status} onChange={(e) => quickStatus(p.id, e.target.value)} className="border border-zinc-200 rounded-md text-xs px-1.5 py-1 focus:border-teal-600 outline-none" data-testid={`inventory-status-${p.inventory_id}`}>
                    {STATUSES.filter((s) => s !== "all").map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                </td>
                <td className="p-3">{money(p.cost_price)}</td>
                <td className="p-3">{money(p.selling_price)}</td>
                <td className="p-3 text-teal-700 font-medium">{p.gross_margin_pct}%</td>
                <td className="p-3 text-zinc-500">{daysAgo(p.date_acquired)}d</td>
                <td className="p-3">
                  <button
                    onClick={() => toggleEditorsPick(p.id, p.is_editors_pick)}
                    title="Feature in homepage Shop The Look"
                    className={`inline-flex ${p.is_editors_pick ? "text-teal-600" : "text-zinc-300 hover:text-zinc-400"}`}
                    data-testid={`editors-pick-toggle-${p.inventory_id}`}
                  >
                    <Star size={16} fill={p.is_editors_pick ? "currentColor" : "none"} />
                  </button>
                </td>
                <td className="p-3 flex gap-3">
                  <button onClick={() => openEdit(p.id)} className="text-xs text-teal-700 hover:text-teal-800 font-heading font-semibold uppercase" data-testid={`edit-${p.inventory_id}`}>Edit</button>
                  <button onClick={() => duplicate(p.id)} className="text-xs text-zinc-500 hover:text-zinc-800 font-heading font-semibold uppercase" data-testid={`duplicate-${p.inventory_id}`}>Duplicate</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="admin-create-product-modal">
          <form onSubmit={submitCreate} className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <h2 className="font-serif text-2xl mb-6 text-zinc-900">New Product</h2>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <select value={form.brand_slug} onChange={(e) => setForm({ ...form, brand_slug: e.target.value })} className={inputCls} data-testid="create-brand-select">
                {brands.map((b) => <option key={b.slug} value={b.slug}>{b.name}</option>)}
              </select>
              <input required placeholder="Model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className={inputCls} data-testid="create-model-input" />
              <input placeholder="Model number" value={form.model_number} onChange={(e) => setForm({ ...form, model_number: e.target.value })} className={inputCls} />
              <input required placeholder="Colour" value={form.colour} onChange={(e) => setForm({ ...form, colour: e.target.value, frame_colour: e.target.value })} className={inputCls} data-testid="create-colour-input" />
              <input placeholder="Frame material" value={form.frame_material} onChange={(e) => setForm({ ...form, frame_material: e.target.value })} className={inputCls} />
              <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className={inputCls}>
                {["Men's", "Women's", "Unisex"].map((g) => <option key={g}>{g}</option>)}
              </select>
              <input placeholder="Shape" value={form.shape} onChange={(e) => setForm({ ...form, shape: e.target.value })} className={inputCls} />
              <input placeholder="Style" value={form.style} onChange={(e) => setForm({ ...form, style: e.target.value })} className={inputCls} />
              <input type="number" required placeholder="Selling price" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} className={inputCls} data-testid="create-price-input" />
              <input type="number" placeholder="Cost price" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} className={inputCls} data-testid="create-cost-input" />
              <input type="number" placeholder="Original RRP" value={form.original_rrp} onChange={(e) => setForm({ ...form, original_rrp: e.target.value })} className={inputCls} />
              <input type="number" min={1} max={10} placeholder="Condition score" value={form.condition_score} onChange={(e) => setForm({ ...form, condition_score: e.target.value })} className={inputCls} data-testid="create-condition-input" />
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                {STATUSES.filter((s) => s !== "all").map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
              <input placeholder="Location (e.g. A-04)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputCls} />
              <input type="number" placeholder="Lens width (mm)" value={form.lens_width} onChange={(e) => setForm({ ...form, lens_width: e.target.value })} className={inputCls} data-testid="create-lens-width-input" />
              <input type="number" placeholder="Bridge width (mm)" value={form.bridge_width} onChange={(e) => setForm({ ...form, bridge_width: e.target.value })} className={inputCls} data-testid="create-bridge-width-input" />
              <input type="number" placeholder="Temple length (mm)" value={form.temple_length} onChange={(e) => setForm({ ...form, temple_length: e.target.value })} className={inputCls} data-testid="create-temple-length-input" />
              <input type="number" placeholder="Overall frame width (mm)" value={form.overall_frame_width} onChange={(e) => setForm({ ...form, overall_frame_width: e.target.value })} className={inputCls} data-testid="create-overall-width-input" />
            </div>
            <p className="text-xs text-zinc-400 mb-4">Add product photos after creating, from the Edit screen.</p>
            <div className="flex gap-3">
              <button type="submit" className="bg-teal-600 hover:bg-teal-700 text-ink px-6 py-3 text-xs font-heading font-semibold uppercase rounded-full transition-colors" data-testid="create-product-submit">Create Product</button>
              <button type="button" onClick={() => setShowCreate(false)} className="border border-zinc-200 hover:border-zinc-400 px-6 py-3 text-xs font-heading font-semibold uppercase rounded-full text-zinc-700 transition-colors">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {editingId && editForm && (
        <div className="fixed inset-0 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="admin-edit-product-modal">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <h2 className="font-serif text-2xl mb-1 text-zinc-900">{editForm.brand_name} {editForm.model}</h2>
            <p className="text-xs text-zinc-400 mb-6 font-mono">{editForm.inventory_id}</p>

            <div className="grid grid-cols-3 gap-3 mb-6">
              <label className="block text-sm text-zinc-600">Selling price
                <input type="number" value={editForm.selling_price} onChange={(e) => setEditForm({ ...editForm, selling_price: e.target.value })} className={`${inputCls} w-full mt-1`} data-testid="edit-price-input" />
              </label>
              <label className="block text-sm text-zinc-600">Cost price
                <input type="number" value={editForm.cost_price} onChange={(e) => setEditForm({ ...editForm, cost_price: e.target.value })} className={`${inputCls} w-full mt-1`} data-testid="edit-cost-input" />
              </label>
              <label className="block text-sm text-zinc-600">Condition score
                <input type="number" min={1} max={10} value={editForm.condition_score} onChange={(e) => setEditForm({ ...editForm, condition_score: e.target.value })} className={`${inputCls} w-full mt-1`} data-testid="edit-condition-input" />
              </label>
            </div>

            <h3 className="font-heading text-xs uppercase tracking-wide mb-3 text-zinc-500">Measurements (mm)</h3>
            <div className="grid grid-cols-4 gap-3 mb-6">
              <label className="block text-sm text-zinc-600">Lens
                <input type="number" value={editForm.lens_width || ""} onChange={(e) => setEditForm({ ...editForm, lens_width: e.target.value })} className={`${inputCls} w-full mt-1`} data-testid="edit-lens-width-input" />
              </label>
              <label className="block text-sm text-zinc-600">Bridge
                <input type="number" value={editForm.bridge_width || ""} onChange={(e) => setEditForm({ ...editForm, bridge_width: e.target.value })} className={`${inputCls} w-full mt-1`} data-testid="edit-bridge-width-input" />
              </label>
              <label className="block text-sm text-zinc-600">Temple
                <input type="number" value={editForm.temple_length || ""} onChange={(e) => setEditForm({ ...editForm, temple_length: e.target.value })} className={`${inputCls} w-full mt-1`} data-testid="edit-temple-length-input" />
              </label>
              <label className="block text-sm text-zinc-600">Overall width
                <input type="number" value={editForm.overall_frame_width || ""} onChange={(e) => setEditForm({ ...editForm, overall_frame_width: e.target.value })} className={`${inputCls} w-full mt-1`} data-testid="edit-overall-width-input" />
              </label>
            </div>

            <h3 className="font-heading text-xs uppercase tracking-wide mb-3 text-zinc-500">RRP Evidence</h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input placeholder="RRP source (e.g. Sunglass Hut AU)" value={editForm.rrp_source || ""} onChange={(e) => setEditForm({ ...editForm, rrp_source: e.target.value })} className={inputCls} data-testid="edit-rrp-source-input" />
              <input placeholder="Source URL" value={editForm.rrp_source_url || ""} onChange={(e) => setEditForm({ ...editForm, rrp_source_url: e.target.value })} className={inputCls} data-testid="edit-rrp-url-input" />
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-700 mb-6" data-testid="edit-rrp-verified-toggle">
              <input type="checkbox" checked={!!editForm.rrp_verified} onChange={(e) => setEditForm({ ...editForm, rrp_verified: e.target.checked })} className="accent-teal-600" data-testid="edit-rrp-verified-toggle" />
              RRP verified — show "Save $X / Y% Off" claim on this product
            </label>

            <h3 className="font-heading text-xs uppercase tracking-wide mb-3 text-zinc-500">The Lookers Inspection</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
              {INSPECTION_FIELDS.map(([key, label]) => (
                <label key={key} className="flex items-center gap-1.5 text-xs text-zinc-700" data-testid={`edit-inspection-${key}`}>
                  <input
                    type="checkbox"
                    checked={!!editForm.inspection?.[key]}
                    onChange={(e) => setEditForm({ ...editForm, inspection: { ...editForm.inspection, [key]: e.target.checked } })}
                    className="accent-teal-600"
                  />
                  {label}
                </label>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 mb-6">
              <label className="flex items-center gap-2 text-sm text-zinc-700">
                <input type="checkbox" checked={!!editForm.original_case_included} onChange={(e) => setEditForm({ ...editForm, original_case_included: e.target.checked })} className="accent-teal-600" data-testid="edit-original-case-toggle" />
                Original case included
              </label>
              <input placeholder="What's included (comma separated)" value={(editForm.whats_included || []).join(", ")} onChange={(e) => setEditForm({ ...editForm, whats_included: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className={inputCls} data-testid="edit-whats-included-input" />
            </div>
            <textarea placeholder="Condition notes (written explanation shown to customers)" value={editForm.condition_notes || ""} onChange={(e) => setEditForm({ ...editForm, condition_notes: e.target.value })} className={`${inputCls} w-full mb-6`} rows={2} data-testid="edit-condition-notes-input" />

            <h3 className="font-heading text-xs uppercase tracking-wide mb-3 text-zinc-500">Authenticity</h3>
            <div className="grid grid-cols-2 gap-3 mb-2">
              <select value={editForm.authentication_method || "Lookers Authentication Check"} onChange={(e) => setEditForm({ ...editForm, authentication_method: e.target.value })} className={inputCls} data-testid="edit-authentication-method">
                {AUTH_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              {editForm.authentication_method === "Third-Party Authentication" && (
                <input placeholder="Authentication provider" value={editForm.authentication_provider || ""} onChange={(e) => setEditForm({ ...editForm, authentication_provider: e.target.value })} className={inputCls} data-testid="edit-authentication-provider" />
              )}
            </div>
            {editForm.authentication_method === "Third-Party Authentication" && (
              <div className="grid grid-cols-2 gap-3 mb-6">
                <input placeholder="Reference / certificate number" value={editForm.authentication_reference || ""} onChange={(e) => setEditForm({ ...editForm, authentication_reference: e.target.value })} className={inputCls} data-testid="edit-authentication-reference" />
                <input placeholder="Verification URL" value={editForm.authentication_verification_url || ""} onChange={(e) => setEditForm({ ...editForm, authentication_verification_url: e.target.value })} className={inputCls} data-testid="edit-authentication-verification-url" />
              </div>
            )}
            <p className="text-xs text-zinc-400 mb-6">Only choose "Third-Party Authentication" when an outside specialist has verified this exact frame — the product page will then show "Independently Authenticated".</p>

            <h3 className="font-heading text-xs uppercase tracking-wide mb-3 text-zinc-500">Product Photos ({(editForm.images || []).length}/{MAX_PHOTOS})</h3>
            <div className="grid grid-cols-5 gap-3 mb-4">
              {(editForm.images || []).map((img, i) => (
                <div key={img.id} className="relative group focus-within:opacity-100" data-testid={`edit-image-${i}`}>
                  <img src={img.url} alt="" className="w-full aspect-square object-cover rounded-lg border border-zinc-200" />
                  <div className="absolute inset-x-0 bottom-0 flex justify-between p-1 bg-black/50 rounded-b-lg opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                    <button type="button" onClick={() => moveEditImage(i, -1)} disabled={i === 0} className="text-white text-xs px-1 disabled:opacity-30" data-testid={`move-image-left-${i}`}>‹</button>
                    <button type="button" onClick={() => removeEditImage(img.id)} className="text-white text-xs px-1" data-testid={`remove-image-${i}`}>✕</button>
                    <button type="button" onClick={() => moveEditImage(i, 1)} disabled={i === (editForm.images.length - 1)} className="text-white text-xs px-1 disabled:opacity-30" data-testid={`move-image-right-${i}`}>›</button>
                  </div>
                  {i === 0 && <span className="absolute top-1 left-1 bg-teal-600 text-ink text-[9px] font-heading uppercase px-1.5 py-0.5 rounded">Main</span>}
                </div>
              ))}
              {(editForm.images || []).length < MAX_PHOTOS && (
                <label className="border-2 border-dashed border-zinc-200 rounded-lg aspect-square flex items-center justify-center text-xs text-zinc-400 cursor-pointer hover:border-teal-500 hover:text-teal-600 transition-colors" data-testid="add-image-button">
                  {uploadingImg ? "Uploading…" : "+ Add Photo"}
                  <input ref={editFileRef} type="file" accept="image/*" multiple onChange={uploadEditImages} className="hidden" data-testid="edit-image-file-input" />
                </label>
              )}
            </div>
            <p className="text-xs text-zinc-400 mb-6">Upload 4–5 photos for the best product-page gallery. The first photo is used as the main image; products with 3+ photos get an automatic 360° drag view.</p>

            <div className="flex gap-3">
              <button type="button" onClick={saveEdit} className="bg-teal-600 hover:bg-teal-700 text-ink px-6 py-3 text-xs font-heading font-semibold uppercase rounded-full transition-colors" data-testid="save-edit-product-button">Save Changes</button>
              <button type="button" onClick={() => setEditingId(null)} className="border border-zinc-200 hover:border-zinc-400 px-6 py-3 text-xs font-heading font-semibold uppercase rounded-full text-zinc-700 transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
